// btw-front/src/pages/PartnerNoticeDetail.jsx
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { Badge } from "../components/ui/Badge";
import { EmptyState } from "../components/ui/EmptyState";
import { get as getNotice, downloadAttachment } from "../api/notices";

const MB = 1024 * 1024;
function formatSize(bytes) {
  if (bytes == null) return "";
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

export default function PartnerNoticeDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    getNotice(id)
      .then((n) => {
        if (!cancelled) setNotice(n);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleDownload(a) {
    try {
      await downloadAttachment(id, a.id, a.originalName);
    } catch (e) {
      message.error(e.message || t("partners.notices.detail.downloadFailed"));
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <div className="mx-auto max-w-3xl">
            <Link to="/partners/notices" className="btw-btn-ghost">
              {t("partners.notices.detail.backToList")}
            </Link>

            {loading ? (
              <p className="mt-8 text-center text-sm text-slate-400">{t("common.loading")}</p>
            ) : notFound || !notice ? (
              <div className="mt-6">
                <EmptyState title={t("partners.notices.detail.notFound")} />
              </div>
            ) : (
              <>
                <header className="mt-5">
                  <div className="flex flex-wrap items-center gap-2">
                    {notice.isPinned && (
                      <Badge tone="neutral">{t("partners.notices.pinnedTag")}</Badge>
                    )}
                    <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                      {notice.title}
                    </h1>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>
                      {t("partners.notices.detail.author")}: {notice.authorName || "-"}
                    </span>
                    <span>
                      {t("partners.notices.detail.createdAt")}:{" "}
                      {notice.createdAt && String(notice.createdAt).slice(0, 10)}
                    </span>
                    <span>
                      {t("partners.notices.detail.views")}: {notice.viewCount || 0}
                    </span>
                  </div>
                </header>

                <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                    {notice.content}
                  </p>
                </section>

                <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("partners.notices.detail.attachments")}
                  </h2>
                  {notice.attachments && notice.attachments.length > 0 ? (
                    <div className="mt-3 flex flex-col gap-1.5">
                      {notice.attachments.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => handleDownload(a)}
                          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-left transition hover:border-accent"
                        >
                          <span
                            className="min-w-0 flex-1 truncate text-sm text-accent"
                            title={a.originalName}
                          >
                            {a.originalName}
                          </span>
                          <span className="shrink-0 text-[11px] text-slate-500">
                            {formatSize(a.sizeBytes)}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-400">
                      {t("partners.notices.detail.noAttachments")}
                    </p>
                  )}
                </section>
              </>
            )}
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
